import React, { ReactElement, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

// import Debug from "debug";
import { Grid } from "@screencloud/screencloud-ui-components";
import StreamSelection from "../../components/StreamSelection/StreamSelection";
import StreamLanding from "../../components/StreamLanding/StreamLanding";
import StreamNamingModal from "../../components/StreamNamingModal/StreamNamingModal";
import DeleteChannelWarningModal from "../../components/DeleteWarning/DeleteChannelWarningModal";
import { getRegionFromParentUrl } from "../../utils/regionUtils";

import {
  AuthMode,
  Stream,
  ChannelStore,
  EditorStore,
  PayloadChannelCreateOne,
} from "../../types";

import {
  endDelete,
  deleteChannel,
  endCreate,
  createChannel,
  endRename,
  renameChannel,
} from "../../actions";
import { RootState } from "../../reducers";

import "./StreamCreateView.scss";
import { ScreenCloudContext } from "../../ScreenCloudReactApp";

// const debug = Debug("broadcast:components:StreamCreateView");

interface StreamCreateViewProps {
  orgId: string;
  spaceId: string;
}

const checkStreamNameExists = (
  streams: Stream[] = [],
  streamName: string
): boolean => {
  return !(
    !streams.length || !streams.find((stream) => stream.name === streamName)
  );
};

const StreamCreateView = (
  props: StreamCreateViewProps
): ReactElement<StreamCreateViewProps> => {
  const { orgId, spaceId } = props;

  const { logger } = useContext(ScreenCloudContext);
  const dispatch = useDispatch();

  const channels: ChannelStore = useSelector(
    (state: RootState) => state.channels
  );
  const editor: EditorStore = useSelector((state: RootState) => state.editor);

  // below helps resolve an issue where a state update would be attempted
  // on an unmounted component
  const unmounted = useRef(false);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  const createStreamAction = async (name: string): Promise<void> => {
    const streamConfig: PayloadChannelCreateOne = {
      name,
      screenCloudOrgId: orgId,
      authMode: AuthMode.Private,
      spaceId: spaceId,
      region: getRegionFromParentUrl(),
    };

    dispatch(endCreate());
    dispatch(createChannel(streamConfig, logger));
  };

  const deleteStreamConfirmation = (): void => {
    const channel = editor.deleteChannel;

    if (channel !== null) {
      const { screenCloudOrgId, spaceId, channelId } = channel;
      dispatch(endDelete(null));
      dispatch(
        deleteChannel({ orgId: screenCloudOrgId, spaceId, channelId }, logger)
      );
    }
  };

  const renameStreamConfirmation = (name: string): void => {
    const channel = editor.renameChannel;

    if (channel !== null) {
      const { screenCloudOrgId, spaceId, channelId } = channel;
      dispatch(endRename(null));
      dispatch(
        renameChannel({ screenCloudOrgId, spaceId, channelId, name }, logger)
      );
    }
  };

  const closeRename = (): void => {
    dispatch(endRename(null));
  };

  const closeDelete = (): void => {
    dispatch(endDelete(null));
  };

  return (
    <div className="setup-container">
      <Grid className="setup-create container">
        {channels.available.length > 0 ? (
          <StreamSelection />
        ) : (
          <StreamLanding />
        )}

        <StreamNamingModal
          isOpen={editor.createChannel}
          checkStreamExists={(streamName) =>
            checkStreamNameExists(channels.available, streamName)
          }
          onCreateClick={createStreamAction}
          onClose={() => dispatch(endCreate())}
          kind={"CREATE"}
        />
        <StreamNamingModal
          isOpen={editor.renameChannel !== null}
          checkStreamExists={(streamName) =>
            checkStreamNameExists(channels.available, streamName)
          }
          onCreateClick={renameStreamConfirmation}
          onClose={closeRename}
          kind={"RENAME"}
          existingName={editor.renameChannel ? editor.renameChannel.name : ""}
        />
        <DeleteChannelWarningModal
          isOpen={editor.deleteChannel !== null}
          onClose={closeDelete}
          onCancel={closeDelete}
          onConfirm={deleteStreamConfirmation}
        />
      </Grid>
    </div>
  );
};

export default StreamCreateView;
